import {
  Component,
  Input,
  Output,
  OnInit,
  OnDestroy,
  EventEmitter,
  forwardRef,
} from '@angular/core'
import {
  NG_VALUE_ACCESSOR,
  ControlValueAccessor,
  FormGroup,
  FormBuilder,
} from '@angular/forms'

import {Subject, BehaviorSubject} from 'rxjs'

import {BsModalService} from 'ngx-bootstrap/modal'

import {ContractService} from '@app/core/services/rest-api'
import {LoggerService} from '@app/core/services/logger.service'

import {Contract} from '@app/core/services/rest-api'

import {ContractSelectorNewModalComponent} from './contract-selector-new-modal.component'
import {takeUntil, take} from 'rxjs/operators'

interface DomainContracts {
  domain: string
  contracts: Contract[]
}

@Component({
  selector: 'contract-selector',
  templateUrl: './contract-selector.component.html',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => ContractSelectorComponent),
      multi: true,
    },
  ],
  standalone: false,
})
export class ContractSelectorComponent
  implements OnInit, OnDestroy, ControlValueAccessor
{
  domains: DomainContracts[] = []

  @Output() contracts: EventEmitter<Contract[]> = new EventEmitter<Contract[]>()

  private _domain: BehaviorSubject<string> = new BehaviorSubject<string>(null)
  private _contractsLoaded: EventEmitter<void> = new EventEmitter<void>()

  @Input()
  set domain(domain: string) {
    this._domain.next(domain)
  }
  get domain(): string {
    return this._domain.getValue()
  }

  @Input()
  multiple: boolean = true

  @Input()
  exclude: Contract[] = []

  private selectedContracts: Contract[] = []

  form: FormGroup

  constructor(
    private contractSvc: ContractService,
    private formBuilder: FormBuilder,
    private modalService: BsModalService,
    private logger: LoggerService,
  ) {}

  ngOnInit() {
    this.form = this.formBuilder.group({})
    this._domain.pipe(takeUntil(this.end$)).subscribe((domain: string) => {
      // TODO retrieve all the available contracts
      this.contractSvc
        .getAvailableContracts(domain)
        .subscribe((contracts: Contract[]) => {
          let domains = new Map<string, DomainContracts>()
          //set current domain by default
          domains.set(domain, {domain: domain, contracts: []})
          contracts
            .filter((contract) => {
              let found = this.exclude.find((c) => c.id == contract.id)
              return found == null
            })
            .sort((a: Contract, b: Contract) => {
              if (a.type.code > b.type.code) return -1
              if (a.type.code < b.type.code) return 1
              return 0
            })
            .forEach((contract) => {
              let dc = domains.get(contract.domain)
              if (dc == null) {
                dc = {
                  domain: contract.domain,
                  contracts: [],
                }
                domains.set(contract.domain, dc)
              }
              dc.contracts.push(contract)
            })
          this.domains = Array.from(domains.values())

          //notify internally that the contracts have been loaded
          this._contractsLoaded.emit()

          //filter the currently selected contracts
          this.selectedContracts = this.filterValidContracts(
            this.selectedContracts,
          )

          //convert the domain contracts into formControls in the form
          if (!this.form) {
            this.form = this.formBuilder.group({})
          }
          this.domains.forEach((dc) => {
            dc.contracts.forEach((contract) => {
              let id = '' + contract.id
              if (this.form.get(id) === null) {
                let selected = this.selectedContracts.includes(contract)
                let control = this.formBuilder.control(selected)
                control.valueChanges
                  .pipe(takeUntil(this.end$))
                  .subscribe((value) => {
                    if (value) {
                      this.select(contract)
                    } else {
                      this.remove(contract)
                    }
                  })
                this.form.addControl(id, control)
              }
            })
          })
        })
    })
  }

  private end$: Subject<void> = new Subject<void>()
  ngOnDestroy() {
    this.end$.next()
    this.end$.complete()
    this.contracts.complete()
  }

  select(contract: Contract) {
    if (!this.multiple) {
      this.selectedContracts.length = 0
      for (let key in this.form.controls) {
        if (key != '' + contract.id) {
          this.form.controls[key].setValue(false)
        }
      }
    }
    this.selectedContracts.push(contract)
    this.contracts.emit(this.selectedContracts)
  }

  remove(contract: Contract) {
    let idx = this.selectedContracts.indexOf(contract)
    this.selectedContracts.splice(idx, 1)
    this.contracts.emit(this.selectedContracts)
  }

  isSelected(contract: Contract) {
    return this.selectedContracts.includes(contract)
  }

  private filterValidContracts(contracts: Contract[]): Contract[] {
    return contracts.filter((contract: Contract) => {
      for (let domain of this.domains) {
        if (domain.contracts.find((c) => c.id == contract.id)) {
          return true
        }
      }
      return false
    })
  }

  writeValue(contracts: Contract[]) {
    //TODO validate contracts
    if (contracts == null) {
      contracts = []
      Object.keys(this.form.controls).forEach((key) => {
        this.form.controls[key].setValue(false)
      })
    }
    this.selectedContracts = this.filterValidContracts(contracts)
    this.contracts.emit(this.selectedContracts)
  }

  registerOnChange(_fn) {
    this.contracts.pipe(takeUntil(this.end$)).subscribe((contracts) => {
      _fn(contracts)
    })
  }

  registerOnTouched(_fn) {
    this.contracts.pipe(takeUntil(this.end$)).subscribe((contracts) => {
      _fn()
    })
  }

  showAddContract(domain: string) {
    if (!!domain) {
      domain = this._domain.getValue()
    }
    let modalRef = this.modalService.show(ContractSelectorNewModalComponent, {
      class: 'modal-lg',
      initialState: {initialCode: domain},
    })
    var sub = modalRef.content.onClose.subscribe((value) => {
      //we need to select the contract after a reload.
      this._contractsLoaded.pipe(take(1)).subscribe(() => {
        for (let domain of this.domains) {
          for (let contract of domain.contracts) {
            if (contract.id == value.id) {
              //compare by id
              this.select(contract)
            }
          }
        }
      })

      /* Update domain to same value, this forces the contracts list to reload */
      this._domain.next(this._domain.getValue())

      sub.unsubscribe()
    })
  }
}
